// src/admin/pages/reservation/reservationService.ts
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query,
  orderBy, where, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { Reservation, ReservationStatus, InquiryType } from '../../../enums/reservation';
import { AuditService } from '../audit/auditService';

const COLLECTION = 'reservations';

const mapDoc = (id: string, data: any): Reservation => ({
  id,
  inquiryType: data.inquiryType || 'General Inquiry',
  carId: data.carId || '',
  carName: data.carName || '',
  carImage: data.carImage || '',
  carPrice: data.carPrice || 0,
  brandId: data.brandId || undefined,
  brandName: data.brandName || undefined,
  modelId: data.modelId || undefined,
  modelName: data.modelName || undefined,
  customerName: data.customerName || '',
  customerPhone: data.customerPhone || '',
  customerEmail: data.customerEmail || '',
  preferredBranchId: data.preferredBranchId || '',
  preferredBranchName: data.preferredBranchName || '',
  preferredDate: data.preferredDate || '',
  preferredTime: data.preferredTime || '',
  message: data.message || '',
  status: data.status || 'New Inquiry',
  depositAmount: data.depositAmount || 0,
  paymentMethod: data.paymentMethod || 'Not Paid Yet',
  deliveryMethod: data.deliveryMethod || undefined,
  cancellationReason: data.cancellationReason || undefined,
  decisionReason: data.decisionReason || undefined,
  assignedToUserId: data.assignedToUserId || '',
  assignedToName: data.assignedToName || '',
  internalNotes: data.internalNotes || '',
  finalSalePrice: data.finalSalePrice || 0,
  saleFinalizedAt: data.saleFinalizedAt?.toDate?.() || null,
  createdAt: data.createdAt?.toDate?.() || new Date(),
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
});

export interface StaffReservationInput {
  inquiryType: InquiryType;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  // A specific in-stock vehicle takes precedence; otherwise brand/model (or
  // neither, for "any vehicle") — same shape as the client site's test drive
  // booking form (see client/src/pages/Services/reservationService.ts).
  carId?: string;
  carName?: string;
  carImage?: string;
  carPrice?: number;
  brandId?: string;
  brandName?: string;
  modelId?: string;
  modelName?: string;
  preferredDate?: string;
  preferredTime?: string;
  message?: string;
}

export class ReservationService {
  /** Called from the CLIENT site — no auth required, this is a public lead-capture write. */
  static async submitPublicInquiry(data: Omit<Reservation, 'id' | 'status' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const ref = await addDoc(collection(db, COLLECTION), {
      ...data,
      status: 'New Inquiry',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  }

  /** Staff manually recording a reservation/test-drive request taken over the
   *  phone or in person — same shape as a public inquiry, just entered from
   *  the admin app instead of the client site. */
  static async create(data: StaffReservationInput, userId: string, userName: string): Promise<string> {
    const ref = await addDoc(collection(db, COLLECTION), {
      inquiryType: data.inquiryType,
      customerName: data.customerName,
      customerPhone: data.customerPhone,
      customerEmail: data.customerEmail || '',
      carId: data.carId || '',
      carName: data.carName || '',
      carImage: data.carImage || '',
      carPrice: data.carPrice || 0,
      brandId: data.brandId || '',
      brandName: data.brandName || '',
      modelId: data.modelId || '',
      modelName: data.modelName || '',
      preferredDate: data.preferredDate || '',
      preferredTime: data.preferredTime || '',
      message: data.message || '',
      status: 'New Inquiry',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await AuditService.log({
      action: 'create',
      module: 'Reservations',
      entityId: ref.id,
      entityLabel: data.customerName,
      description: `Added a new ${data.inquiryType.toLowerCase()} for ${data.customerName} (entered by staff)`,
      userId,
      userName,
    });

    return ref.id;
  }

  static async getAll(): Promise<Reservation[]> {
    const snap = await getDocs(query(collection(db, COLLECTION), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => mapDoc(d.id, d.data()));
  }

  static async getByStatus(status: ReservationStatus): Promise<Reservation[]> {
    const snap = await getDocs(query(collection(db, COLLECTION), where('status', '==', status), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => mapDoc(d.id, d.data()));
  }

  static async getById(id: string): Promise<Reservation | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    if (!snap.exists()) return null;
    return mapDoc(snap.id, snap.data());
  }

  static async update(
    id: string,
    updates: Partial<Reservation>,
    userId: string,
    userName: string
  ): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    const before = snap.exists() ? snap.data() : {};

    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() } as any);

    const statusChanged = updates.status && updates.status !== before.status;
    await AuditService.log({
      action: statusChanged ? 'status_change' : 'update',
      module: 'Reservations',
      entityId: id,
      entityLabel: before.customerName || 'Reservation',
      description: statusChanged
        ? `Updated reservation for ${before.customerName || 'customer'} — status changed to "${updates.status}"`
        : `Updated reservation details for ${before.customerName || 'customer'}`,
      userId,
      userName,
    });

    // If marked as Sold, flip the car's status to "Sold" too, keeping inventory in sync.
    // CarStatus is the single source of truth for a vehicle's availability —
    // there is no separate isOutOfStock flag to set (see answers.text).
    if (updates.status === 'Sold' && before.carId) {
      try {
        const carRef = doc(db, 'products', before.carId);
        await updateDoc(carRef, { status: 'Sold', updatedAt: serverTimestamp() });
      } catch (e) {
        console.error('Could not auto-update car status to Sold:', e);
      }
    }

    // If a reservation is cancelled/lost after having been marked Reserved, release
    // the car back to In Stock so it's sellable again.
    if (
      (updates.status === 'Cancelled' || updates.status === 'Lost') &&
      before.carId &&
      (before.status === 'Reserved (Deposit Paid)' || before.status === 'Sold')
    ) {
      try {
        const carRef = doc(db, 'products', before.carId);
        await updateDoc(carRef, { status: 'In Stock', updatedAt: serverTimestamp() });
      } catch (e) {
        console.error('Could not release car back to In Stock:', e);
      }
    }

    // Once Test Drive Scheduled or later, hold the car as Reserved so it doesn't
    // get sold out from under this customer while the deal is in progress.
    if (
      (updates.status === 'Reserved (Deposit Paid)') &&
      before.carId
    ) {
      try {
        const carRef = doc(db, 'products', before.carId);
        await updateDoc(carRef, { status: 'Reserved', updatedAt: serverTimestamp() });
      } catch (e) {
        console.error('Could not mark car as Reserved:', e);
      }
    }
  }

  static async delete(id: string, userId: string, userName: string): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    await deleteDoc(ref);
    await AuditService.log({
      action: 'delete',
      module: 'Reservations',
      entityId: id,
      entityLabel: data.customerName || 'Reservation',
      description: `Deleted reservation/inquiry from ${data.customerName || 'customer'}`,
      userId,
      userName,
    });
  }

  static async getStats(): Promise<{ total: number; newInquiries: number; reserved: number; sold: number }> {
    const all = await this.getAll();
    return {
      total: all.length,
      newInquiries: all.filter(r => r.status === 'New Inquiry').length,
      reserved: all.filter(r => r.status === 'Reserved (Deposit Paid)').length,
      sold: all.filter(r => r.status === 'Sold').length,
    };
  }
}
